import { Check, LockKeyhole, Plus, RefreshCw, ScanSearch, Search, UsersRound, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ApiError, api } from "../api";
import { useAuth } from "../auth";
import { Button, ProviderMark } from "../components";
import { GroupSharingView, SharingRulesPanel, createRule, ruleErrorMessage, useRuleSuggestions, useSharingRules } from "./SharingRules";
import {
  EMPTY_FILTERS,
  type BulkAction,
  type BulkResult,
  type RuleShape,
  type SharingFilters,
  type SharingGroup,
  type SharingLoop,
  activeAttributeFilters,
  bulkOutcomeLine,
  bulkPlan,
  countLabel,
  filterSharingLoops,
  ruleOffer,
  ruleSentence,
  ruleTitle,
  selectionWithin,
  shareRuleId,
  sharingFacets,
  shownLine,
  sourceLabel,
  toggleId,
  visibleTags,
} from "./sharingBulk";
import "./onboarding.css";
import "../styles/administration.css";

type SharingOverview = { groups: SharingGroup[]; loops: SharingLoop[] };
/** The facts the page header reads: how many loops exist, how many cross into a group, and how many rules stand. */
export type SharingSummary = { loops: number; sharedLoops: number; groups: number; rules: number };
type RuleOffer = RuleShape & { targetOrganizationId: string; targetName: string };
type Outcome = { line: string; offer: RuleOffer | null };

const FACET_ROWS: Array<{ key: keyof SharingFilters & ("tag" | "source" | "kind" | "cadence"); label: string; minimum: number }> = [
  { key: "tag", label: "Tag", minimum: 1 },
  { key: "source", label: "Source", minimum: 2 },
  { key: "kind", label: "Kind", minimum: 2 },
  { key: "cadence", label: "Cadence", minimum: 2 },
];

/** Up to three category tags for a matrix row, then how many more there are. */
function rowTags(loop: SharingLoop): string | null {
  const { shown, more } = visibleTags(loop.tags);
  if (!shown.length) return null;
  return [...shown, ...(more ? [`+${more}`] : [])].join(" · ");
}

/**
 * Per-loop, per-group sharing grants for a personal workspace.
 * Used on the Sharing page and as the last step of source onboarding. Discovery never publishes
 * anything; every switch here is one reversible grant from one loop to one group. At scale the same
 * page filters the catalog by attribute, shares a selection in bulk, and keeps standing rules; every
 * grant a rule makes is labelled and can be withdrawn. In group mode it lists what was shared in.
 */
export function SharingStage({ organizationId, canManage, onChanged, mode = "personal" }: { organizationId: string; canManage: boolean; onChanged?: (summary: SharingSummary) => void; mode?: "personal" | "group" }) {
  if (mode === "group") return <GroupSharingView organizationId={organizationId} />;
  return <PersonalSharingStage organizationId={organizationId} canManage={canManage} onChanged={onChanged} />;
}

function PersonalSharingStage({ organizationId, canManage, onChanged }: { organizationId: string; canManage: boolean; onChanged?: (summary: SharingSummary) => void }) {
  const { refresh } = useAuth();
  const [sharing, setSharing] = useState<SharingOverview>({ groups: [], loops: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [groupName, setGroupName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState<SharingFilters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [targetId, setTargetId] = useState("");
  const [bulkBusy, setBulkBusy] = useState<BulkAction | "">("");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const rulesEnabled = !loading && !loadError && sharing.groups.length > 0;
  const rulesState = useSharingRules(organizationId, rulesEnabled);
  const suggestions = useRuleSuggestions(organizationId, rulesEnabled);

  const load = useCallback(async () => {
    try {
      const result = await api<SharingOverview>("/api/app/sharing", {}, organizationId);
      setSharing(result);
      setLoadError("");
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : "The sharing state could not be read");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Every confirmed change to the grants or the rules reaches the page header as one summary.
  useEffect(() => {
    if (loading || loadError) return;
    onChangedRef.current?.({
      loops: sharing.loops.length,
      sharedLoops: sharing.loops.filter((loop) => loop.sharedTo.length > 0).length,
      groups: sharing.groups.length,
      rules: rulesState.rules.length,
    });
  }, [loading, loadError, sharing, rulesState.rules]);

  const facets = useMemo(() => sharingFacets(sharing.loops), [sharing.loops]);
  const filtered = useMemo(() => filterSharingLoops(sharing.loops, filters), [sharing.loops, filters]);
  const activeSelection = useMemo(() => selectionWithin(selected, filtered.map((loop) => loop.id)), [selected, filtered]);
  const rulesById = useMemo(() => new Map(rulesState.rules.map((rule) => [rule.id, rule])), [rulesState.rules]);
  const hasFilters = Boolean(filters.query.trim()) || activeAttributeFilters(filters).length > 0;
  const target = sharing.groups.find((group) => group.id === targetId) ?? sharing.groups[0] ?? null;
  const plan = useMemo(() => target ? bulkPlan(sharing.loops, activeSelection, target.id) : { share: [], unshare: [] }, [sharing.loops, activeSelection, target]);
  const allVisibleSelected = filtered.length > 0 && filtered.every((loop) => activeSelection.has(loop.id));
  const facetOptions = {
    tag: facets.tags.map((facet) => ({ value: facet.tag, label: facet.tag, count: facet.count })),
    source: facets.sources.map((source) => ({ value: source.id, label: source.name, count: source.count })),
    kind: facets.kinds.map((kind) => ({ value: kind.value, label: kind.value, count: kind.count })),
    cadence: facets.cadences.map((cadence) => ({ value: cadence.value, label: cadence.value, count: cadence.count })),
  };

  function setFilter(key: "tag" | "source" | "kind" | "cadence", value: string) {
    setFilters((current) => ({ ...current, [key]: current[key] === value ? null : value }));
  }

  function selectAllVisible() {
    setSelected(allVisibleSelected ? new Set() : new Set(filtered.map((loop) => loop.id)));
  }

  async function createGroup(event: FormEvent) {
    event.preventDefault();
    if (!groupName.trim()) return;
    setCreating(true);
    setError("");
    try {
      await api("/api/app/groups", { method: "POST", body: JSON.stringify({ name: groupName }) }, organizationId);
      setGroupName("");
      await refresh();
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The group could not be created");
    } finally {
      setCreating(false);
    }
  }

  async function toggleShare(loop: SharingLoop, group: SharingGroup) {
    const key = `${loop.id}:${group.id}`;
    const shared = loop.sharedTo.includes(group.id);
    setBusyKey(key);
    setError("");
    try {
      if (shared) {
        await api(`/api/app/sharing/${loop.id}/${group.id}`, { method: "DELETE" }, organizationId);
      } else {
        await api("/api/app/sharing", { method: "POST", body: JSON.stringify({ loopId: loop.id, targetOrganizationId: group.id }) }, organizationId);
      }
      setSharing((current) => ({
        ...current,
        loops: current.loops.map((candidate) => candidate.id === loop.id ? {
          ...candidate,
          sharedTo: shared ? candidate.sharedTo.filter((id) => id !== group.id) : [...candidate.sharedTo, group.id],
          shares: shared ? candidate.shares?.filter((share) => share.targetOrganizationId !== group.id) : [...(candidate.shares ?? []), { targetOrganizationId: group.id, ruleId: null }],
        } : candidate),
      }));
      if (shared && shareRuleId(loop, group.id)) void rulesState.refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The sharing choice could not be saved");
    } finally {
      setBusyKey("");
    }
  }

  async function bulk(action: BulkAction) {
    const ids = action === "share" ? plan.share : plan.unshare;
    if (!ids.length || !target) return;
    setBulkBusy(action);
    setError("");
    setOutcome(null);
    try {
      const result = await api<BulkResult>("/api/app/sharing/bulk", { method: "POST", body: JSON.stringify({ loopIds: ids, targetOrganizationId: target.id, action }) }, organizationId);
      const offered = action === "share" ? ruleOffer(filters) : null;
      const offerable = offered && (offered.attribute !== "source" || sharing.loops.some((loop) => loop.sourceId)) && !rulesState.rules.some((rule) => rule.targetOrganizationId === target.id && rule.attribute === offered.attribute && rule.value.toLowerCase() === offered.value.toLowerCase());
      setOutcome({
        line: bulkOutcomeLine(result, action, target.name),
        offer: offerable ? { ...offered, sourceName: offered.attribute === "source" ? facets.sources.find((source) => source.id === offered.value)?.name ?? null : null, targetOrganizationId: target.id, targetName: target.name } : null,
      });
      setSelected(new Set());
      await load();
      await rulesState.refresh();
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status === 404 ? "Bulk sharing is not available yet. Loops can still be shared one at a time." : caught instanceof Error ? caught.message : "The bulk sharing change did not finish");
    } finally {
      setBulkBusy("");
    }
  }

  async function makeRule(offer: RuleOffer) {
    setOfferBusy(true);
    setError("");
    try {
      await createRule(organizationId, { targetOrganizationId: offer.targetOrganizationId, attribute: offer.attribute, value: offer.value });
      setOutcome({ line: `Rule created: ${ruleSentence(offer, offer.targetName)}.`, offer: null });
      await rulesState.refresh();
      await load();
    } catch (caught) {
      setError(ruleErrorMessage(caught, "The rule could not be created"));
    } finally {
      setOfferBusy(false);
    }
  }

  const privateCount = sharing.loops.filter((loop) => !loop.sharedTo.length).length;
  const selectable = canManage && sharing.groups.length > 0;

  return (
    <div className="sharing-host">
      <section className="sharing-stage" id="sharing" aria-labelledby="sharing-title">
        <header>
          <h2 className="eyebrow" id="sharing-title">Loops</h2>
          {!loading && !loadError && <span className="sharing-stage__lock"><LockKeyhole size={12} aria-hidden="true" /> {privateCount} private</span>}
        </header>

        {loading ? (
          <div className="sharing-empty" role="status"><RefreshCw className="spin" size={20} aria-hidden="true" /><div><b>Reading your sharing state.</b><p>Groups and grants appear as soon as they load.</p></div></div>
        ) : loadError ? (
          <div className="sharing-empty" role="alert"><LockKeyhole size={20} aria-hidden="true" /><div><b>Sharing state unavailable.</b><p>{loadError}</p></div><Button variant="secondary" onClick={() => void load()}>Try again</Button></div>
        ) : sharing.groups.length ? (
          sharing.loops.length ? <>
            <div className="sharing-filters" role="search">
              <label className="sharing-filters__search"><Search size={15} aria-hidden="true" /><span className="sr-only">Search loops</span><input value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} placeholder="Search loops, sources, kinds, or tags" /></label>
              <span className="sharing-filters__count" aria-live="polite">{shownLine(filtered.length, sharing.loops.length)}</span>
              {hasFilters && <button type="button" className="sharing-filters__clear" onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</button>}
              {selectable && filtered.length > 0 && <button type="button" className="sharing-filters__select" onClick={selectAllVisible}>{allVisibleSelected ? "Deselect all" : `Select all ${filtered.length} matching`}</button>}
            </div>
            {FACET_ROWS.map((row) => {
              const options = facetOptions[row.key];
              if (options.length < row.minimum) return null;
              return (
                <div className="sharing-facets" role="group" aria-label={`Filter by ${row.label.toLowerCase()}`} key={row.key}>
                  <span className="sharing-facets__label">{row.label}</span>
                  {options.map((option) => {
                    const pressed = filters[row.key] === option.value;
                    return <button type="button" key={option.value} className={`sharing-chip ${pressed ? "is-selected" : ""}`} aria-pressed={pressed} onClick={() => setFilter(row.key, option.value)}>{option.label} <b>{option.count}</b></button>;
                  })}
                </div>
              );
            })}
            {outcome && (
              <div className="sharing-outcome" role="status">
                <Check size={15} aria-hidden="true" />
                <span>{outcome.line}{outcome.offer && <> <em>{ruleSentence(outcome.offer, outcome.offer.targetName)} from now on?</em></>}</span>
                {outcome.offer && <Button variant="secondary" busy={offerBusy} onClick={() => void makeRule(outcome.offer!)}>Make this a rule</Button>}
                <button type="button" className="icon-button" aria-label="Dismiss" onClick={() => setOutcome(null)}><X size={15} aria-hidden="true" /></button>
              </div>
            )}
            <div className={`sharing-matrix ${selectable ? "sharing-matrix--selectable" : ""}`}>
              <div className="sharing-matrix__head">
                {selectable && <label className="sharing-matrix__check"><input type="checkbox" checked={allVisibleSelected} disabled={!filtered.length} onChange={selectAllVisible} /><span className="sr-only">Select every loop shown</span></label>}
                <span>Your private loops</span><span>Visible in</span>
              </div>
              {filtered.length === 0 && <div className="sharing-empty sharing-empty--filtered"><Search size={20} aria-hidden="true" /><div><b>No loop matches these filters.</b><p>Widen the search or clear a chip; nothing was changed.</p></div><Button variant="secondary" onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</Button></div>}
              {filtered.map((loop) => {
                const checked = activeSelection.has(loop.id);
                const tags = rowTags(loop);
                return (
                  <article key={loop.id} className={checked ? "is-selected" : ""}>
                    {selectable && <label className="sharing-matrix__check"><input type="checkbox" checked={checked} onChange={() => setSelected((current) => toggleId(current, loop.id))} /><span className="sr-only">Select {loop.name}</span></label>}
                    <div><ProviderMark provider={loop.source?.provider} size={26} /><span><b>{loop.name}</b><small>{sourceLabel(loop)} · {loop.cadence}{tags ? <span className="sharing-row-tags"> · {tags}</span> : null}</small></span></div>
                    <div className="sharing-group-choices">{sharing.groups.map((group) => {
                      const shared = loop.sharedTo.includes(group.id);
                      const busy = busyKey === `${loop.id}:${group.id}`;
                      const ruleId = shared ? shareRuleId(loop, group.id) : null;
                      const rule = ruleId ? rulesById.get(ruleId) ?? null : null;
                      const title = ruleId ? (rule ? ruleTitle(rule) : "Shared by a rule") : undefined;
                      return <button type="button" className={shared ? "is-shared" : ""} disabled={busy || !canManage} onClick={() => void toggleShare(loop, group)} key={group.id} aria-pressed={shared} title={title}><i aria-hidden="true">{shared ? <Check size={11} /> : null}</i>{group.name}{ruleId && <em className="sharing-rule-chip">rule</em>}{busy && <small>saving</small>}</button>;
                    })}</div>
                  </article>
                );
              })}
              {selectable && activeSelection.size > 0 && target && (
                <div className="sharing-actions" role="region" aria-label="Bulk sharing">
                  <span className="sharing-actions__count"><b>{activeSelection.size}</b> selected</span>
                  <label className="sharing-actions__group"><span>with</span><select value={target.id} onChange={(event) => setTargetId(event.target.value)} aria-label="Group">{sharing.groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}</select></label>
                  <Button busy={bulkBusy === "share"} disabled={bulkBusy !== "" || !plan.share.length} onClick={() => void bulk("share")}>{countLabel(plan.share.length, "Share")}</Button>
                  <Button variant="secondary" busy={bulkBusy === "unshare"} disabled={bulkBusy !== "" || !plan.unshare.length} onClick={() => void bulk("unshare")}>{countLabel(plan.unshare.length, "Unshare")}</Button>
                  <Button variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
                </div>
              )}
            </div>
          </> : <div className="sharing-empty"><ScanSearch size={24} aria-hidden="true" /><div><b>Catalog a loop first.</b><p>Your group choices will appear beside every loop the moment an agent syncs.</p></div></div>
        ) : <div className="sharing-empty"><UsersRound size={25} aria-hidden="true" /><div><b>Your catalog is personal. Create a group when you want company.</b><p>A group gets people and teams, but sees no private loops until you choose them here.</p></div></div>}

        {canManage && (
          <form className="sharing-create" onSubmit={(event) => void createGroup(event)}>
            <label><span>{sharing.groups.length ? "Create another group" : "Create your first group"}</span><input value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder="Product, Acme, Research…" minLength={2} required /></label>
            <Button type="submit" variant="secondary" busy={creating} disabled={!groupName.trim()}><Plus size={14} aria-hidden="true" /> Create group</Button>
          </form>
        )}
        {error && <div className="form-error journey-inline-error" role="alert"><strong>That sharing change did not finish.</strong><span>{error}</span><small>The last confirmed sharing state remains shown.</small></div>}
        <footer><LockKeyhole size={14} aria-hidden="true" /><span>Unsharing removes the group’s access immediately. The original loop and its run history stay in your private workspace.</span></footer>
      </section>

      {!loading && !loadError && sharing.groups.length > 0 && (
        <SharingRulesPanel organizationId={organizationId} groups={sharing.groups} loops={sharing.loops} canManage={canManage} state={rulesState} suggestions={suggestions} onChanged={load} />
      )}
    </div>
  );
}
